import React, { useState } from 'react';
import { LockKeyhole, Sparkles } from 'lucide-react';

import UpgradePrompt from '@/components/UpgradePrompt';
import { Button } from '@/components/ui/button';
import {
  formatFeatureName,
  getTierByName,
  getUpgradeTier,
  hasFeature,
  useCurrentTier,
} from '@/lib/tiers';

export default function FeatureGate({ feature, children, fallback = null }) {
  const currentTier = useCurrentTier();
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  if (hasFeature(currentTier, feature)) {
    return children;
  }

  if (fallback) {
    return fallback;
  }

  const upgradeTier = getUpgradeTier(currentTier, feature);
  const currentPlan = getTierByName(currentTier);

  return (
    <>
      <div className="relative overflow-hidden rounded-[24px]">
        <div
          aria-hidden="true"
          className="pointer-events-none select-none blur-[2.5px] brightness-75 saturate-50"
        >
          {children}
        </div>

        <div className="absolute inset-0 flex items-center justify-center bg-slate-950/68 backdrop-blur-sm">
          <div className="mx-6 w-full max-w-md rounded-3xl border border-slate-700/80 bg-slate-950/90 p-6 text-center shadow-[0_24px_80px_rgba(2,6,23,0.75)]">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-blue-400/30 bg-blue-500/10 text-blue-300">
              <LockKeyhole className="h-6 w-6" />
            </div>

            <p className="mt-4 text-lg font-semibold text-white">
              Unlock {formatFeatureName(feature)}
            </p>
            <p className="mt-2 text-sm text-slate-400">
              Available on {upgradeTier.name}. Your current plan is {currentPlan.name}.
            </p>

            <div className="mt-4 flex items-center justify-center gap-2 text-xs uppercase tracking-[0.3em] text-slate-500">
              <Sparkles className="h-3.5 w-3.5" />
              Visible preview enabled
            </div>

            <Button
              className="mt-5 bg-blue-600 text-white hover:bg-blue-500"
              onClick={() => setShowUpgradePrompt(true)}
            >
              Upgrade
            </Button>
          </div>
        </div>
      </div>

      <UpgradePrompt
        currentTier={currentTier}
        feature={feature}
        onOpenChange={setShowUpgradePrompt}
        open={showUpgradePrompt}
      />
    </>
  );
}
